<template>
  <div
    v-if="inputs"
    class="row-editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <i class="material-icons mr-2"> bar_chart </i>
        <div>{{ inputs.name || moduleConfigs.name }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :is-objects="true"
        :tabs="tabs"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display Label</h2>
        <div class="body">
          <b-field
            label="Display Label"
            class="input-field"
            message="">
            <b-input v-model="inputs.name" />
          </b-field>
        </div>
      </div>

      <div
        id="dimension"
        class="editor-section">
        <h2>Dimension</h2>
        <div class="body">
          <DimensionTab
            v-model="inputs.dimension"
            :errors="childErrors('dimension', errors)" />
        </div>
      </div>

      <div
        id="order"
        class="editor-section">
        <h2>Order</h2>
        <div class="body">
          <OrderConfigEditor
            v-model="inputs.order"
            :errors="childErrors('order', errors)" />
        </div>
      </div>

      <div
        id="filters"
        class="editor-section">
        <h2>Filters</h2>
        <div class="body">
          <FiltersTab
            v-model="inputs.filters"
            :errors="childErrors('filters', errors)"
            :default-time-range="context.tableDefaultTimeRange || null"
            default-time-range-label="Use table default" />
        </div>
      </div>

      <div
        id="format"
        class="editor-section">
        <h2>Format</h2>
        <div class="body">
          <DataFormatEditor
            v-model="inputs.data_format"
            :errors="childErrors('data_format', errors)"
            :type-override="dimensionDataType" />
        </div>
      </div>

      <div
        id="total-rows"
        class="editor-section">
        <h2>Total Columns</h2>
        <div class="body">
          <TotalRowsTab
            v-model="inputs.table_totals"
            :errors="childErrors('table_totals', errors)"
            :context="context" />
        </div>
      </div>

      <div
        id="header-style"
        class="editor-section">
        <h2>Header Style</h2>
        <div class="body">
          <b-field
            label="Column Width"
            class="input-field"
            message="">
            <b-numberinput
              v-model="inputs.column_width"
              min="0" />
          </b-field>
          <b-field
            class="input-field"
            label="Horizontal Alignment">
            <b-select
              v-model="inputs.header_style.h_align"
              placeholder="Select alignment"
              expanded>
              <option value="left">
                Left
              </option>
              <option value="center">
                Center
              </option>
              <option value="right">
                Right
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import { throttle } from 'lodash'
  import DimensionTab from './DimensionTab.vue'
  import OrderConfigEditor from '@/components/reports/config/modules/common/OrderConfigEditor'
  import FiltersTab from './FiltersTab.vue'
  import DataFormatEditor from '@/components/reports/config/modules/common/DataFormatEditor'
  import TotalRowsTab from './TotalRowsTab.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'DimensionEditor',
    components: {
      Tabs,
      DataFormatEditor,
      DimensionTab,
      FiltersTab,
      TotalRowsTab,
      OrderConfigEditor,
    },
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      context: {
        type: Object,
        default: () => ({}),
      },
      errors: {
        type: Array,
        default: () => [],
      },
    },

    data () {
      return {
        moduleConfigs: {
          name: 'Dimension',
          hideInLibrary: true,
        },
        inputSectionTab: 'display-label',
      }
    },

    computed: {
      dimensionDataType () {
        const key = this.inputs?.dimension?.key
        if (!key) return null
        const options = this.$store.getters['app/reportsConfig/companyDimensionOptions']
        return options?.[key]?.data_type || null
      },
      tabs () {
        return [
          {
            label: 'Display Label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Dimension',
            id: 'dimension',
            hasErrors: _childErrors('dimension', this.errors).length > 0,
          },
          {
            label: 'Order',
            id: 'order',
            hasErrors: _childErrors('order', this.errors).length > 0,
          },
          {
            label: 'Filters',
            id: 'filters',
            hasErrors: _childErrors('filters', this.errors).length > 0,
          },
          {
            label: 'Format',
            id: 'format',
            hasErrors: _childErrors('data_format', this.errors).length > 0,
          },
          {
            label: 'Total Columns',
            id: 'total-rows',
            hasErrors: _childErrors('table_totals', this.errors).length > 0,
          },
          {
            label: 'Header Style',
            id: 'header-style',
            hasErrors: _childErrors('header_style', this.errors).length > 0,
          },
        ]
      },
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
      throttleScroll () {
        return throttle(this.onScroll, 100)
      },
    },

    methods: {
      childErrors: _childErrors,
      onScroll (event) {
        if (this._scrollingToSection) return
        const container = event.target
        const scrollTop = container.scrollTop
        const scrollingDown = scrollTop >= (this._lastScrollTop || 0)
        this._lastScrollTop = scrollTop
        const scrollableDivRect = container.getBoundingClientRect()
        const threshold = 10
        let activeSection = this.tabs[0].id
        for (const tab of this.tabs) {
          const el = container.querySelector(`#${tab.id}`)
          if (!el) continue
          if (el.getBoundingClientRect().top <= scrollableDivRect.top + threshold) {
            activeSection = tab.id
          }
        }
        const currentIndex = this.tabs.findIndex(t => t.id === this.inputSectionTab)
        const newIndex = this.tabs.findIndex(t => t.id === activeSection)
        if (scrollingDown && newIndex >= currentIndex) {
          this.inputSectionTab = activeSection
        } else if (!scrollingDown && newIndex <= currentIndex) {
          this.inputSectionTab = activeSection
        }
      },
      scrollToSection (sectionId) {
        this._scrollingToSection = true
        this.inputSectionTab = sectionId
        const container = this.$el.querySelector('.editor')
        const el = container && container.querySelector(`#${sectionId}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', })
        }
        clearTimeout(this._scrollTimer)
        this._scrollTimer = setTimeout(() => { this._scrollingToSection = false }, 400)
      },
    },
  }
</script>

<style lang="scss" scoped>
.row-editor-container {
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.widget-type {
  display: flex;
  font-weight: 400;
  font-size: 15px;
  align-items: center;
}

.editor {
  padding: 20px;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  flex-direction: column;
  background: #fbfbfb;

  .editor-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;

    h2 {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 10px;
      text-transform: capitalize;
    }

    .body {
      border: $border-1;
      border-radius: 10px;
      padding: 18px;
      background: white;
    }
  }
}

.editor-header {
  border-bottom: $border-1;
  padding: 20px;

  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 18px;
    align-items: center;
    margin-bottom: 15px;
  }
}
</style>
